// Надписи игры: их список и подстановка перевода.
//
// Каждая надпись названа ключом в паке перевода и несёт английский текст. Игра берёт перевод
// выбранного языка, не нашла — берёт английский пак, не нашла и там — свой текст. Поэтому
// пропуск в переводе виден английской надписью, а не пустым местом.
//
// Паки приходят готовыми, см. assets/locales-base. Часть надписей игры ключей в них пока
// не имеет: имена фич и сообщения об ошибках переводчикам не отдавали. Ключ им назначен
// здесь, и с ним они переведутся без правок кода, см. ADR-37.
//
// Перевод один на всю игру и живёт в этом модуле. Его смена будит подписанные текстовые узлы,
// поэтому сцена и текущий раунд продолжаются без перезагрузки страницы.

import { DEFAULT_LOCALE, type Locale } from './locale';

/** Надпись игры: ключ в паке перевода и английский текст на случай, когда перевода нет. */
export interface Phrase {
  /** Ключ в game_translation.json. */
  readonly key: string;

  /** Английский текст. Он же лежит в паке en_GB, если ключ там есть, — это сверяет тест. */
  readonly text: string;
}

/**
 * Все надписи игры.
 *
 * Список закрытый: игра зовёт надпись по имени из него, поэтому опечатку ловит компилятор.
 */
export const PHRASES = {
  /** Подпись поля счёта в нижней полосе. */
  balance: { key: 'balance', text: 'BALANCE' },

  /** Подпись поля ставки там же. */
  stake: { key: 'stake', text: 'STAKE' },

  /** Подпись поля выигрыша там же. */
  lastWin: { key: 'last_win', text: 'LAST WIN' },

  /** Что игрок видит, когда на счету меньше ставки. */
  noFunds: { key: 'not_enough_funds', text: 'NOT ENOUGH FUNDS' },

  /** Приглашение на экране интро: игра ждёт клик, чтобы браузер пустил звук. */
  tapToStart: { key: 'tap_to_start', text: 'TAP TO START' },

  /** Строка об игре под логотипом на том же экране. Текст взят из GDD, раздел 7.3. */
  introTagline: { key: 'intro_tagline', text: '3x3 CLASSIC SLOT WITH THE RED HOT FEATURES. STEAM SPIN, NUDGE RESPINS, WILD MULTIPLIERS AND EVEN MORE AWAITS.' },

  /** Что игрок видит, когда бэкенд не ответил и после повторов. */
  noAnswer: { key: 'no_connection', text: 'No connection to the server.\nCheck your connection.' },

  /** Надпись на кнопке этого сообщения. */
  retry: { key: 'retry_button', text: 'RETRY' },

  /** Надпись над суммой крупного выигрыша, см. GDD, раздел 3.7. */
  bigWin: { key: 'titleBigWin', text: 'BIG WIN' },

  /** Имя фичи респина на заглушке поля, см. GDD, раздел 4.3. */
  nudgeRespin: { key: 'nudge_respin', text: 'NUDGE RESPIN' },

  /** Имя стадии 2 фичи Steam Spin на заглушке поля, см. GDD, раздел 5.2. */
  steamCharging: { key: 'steam_charging', text: 'STEAM CHARGING' },

  /** Объявление бонуса от манометра, попап id_1 в GDD, раздел 7.2. */
  steamSpin: { key: 'steam_spin_activated', text: 'STEAM SPIN\nACTIVATED' },

  /** Объявление бонуса от скаттеров, попап id_2 там же. */
  redHotSpin: { key: 'red_hot_spin_activated', text: 'RED HOT SPIN\nACTIVATED' },

  /** Первая строка поздравления над выигранной суммой, попапы id_4 и id_5 там же. */
  congratulation: { key: 'free_spin_win_congratulations', text: 'CONGRATULATIONS' },

  /** Вторая строка того же поздравления. */
  youWon: { key: 'free_spin_you_won', text: 'YOU WON' },

  /** Надпись на кнопке, которой игрок пускает бонус. */
  spinButton: { key: 'spin_button', text: 'SPIN' },

  /** Надпись на кнопке, которой игрок закрывает поздравление. */
  okButton: { key: 'ok_button', text: 'OK' },

  /** Заголовок попапа с таблицей выплат, см. ui/paytable.ts. */
  paytable: { key: 'paytable_label', text: 'PAYTABLE' },

  /** Заголовок попапа с правилами игры, см. ui/rules.ts. */
  gameRules: { key: 'game_rules_label', text: 'GAME RULES' },

  /** Заголовок окна настроек. */
  settings: { key: 'settings_label', text: 'SETTINGS' },

  /** Заголовок раздела настроек. */
  settingsOptions: { key: 'settings_options', text: 'OPTIONS' },

  /** Заголовок страницы запуска бонусов в демонстрационном режиме. */
  demoMode: { key: 'demo_mode', text: 'DEMO MODE' },

  /** Пояснение к действиям демонстрационной страницы настроек. */
  demoModeHint: { key: 'demo_mode_hint', text: 'START A FEATURE ON THE NEXT SPIN' },

  /** Кнопка запуска Steam Spin в демонстрационном режиме. */
  demoSteamSpin: { key: 'demo_steam_spin', text: 'PLAY STEAM SPIN' },

  /** Кнопка запуска Red Hot Spin в демонстрационном режиме. */
  demoRedHotSpin: { key: 'demo_red_hot_spin', text: 'PLAY RED HOT SPIN' },

  // Страницы про механики этой игры. Ключей в паках у них нет: правила фич переводчикам
  // не отдавали, см. ADR-41. Английский текст сверен с GDD, разделы 4.3, 5 и 6.

  /** Правило Nudge Respin на его странице правил. Заголовок страницы берёт nudgeRespin. */
  nudgeRespinRules: { key: 'nudge_respin_rules', text: 'A Wild on the reels locks that reel and nudges it down one symbol on every free respin. Respins go on until the Wild leaves the field.' },

  /** Имя фичи Steam Spin заголовком страницы. Своё: надпись steamSpin несёт объявление бонуса. */
  steamSpinName: { key: 'steam_spin', text: 'STEAM SPIN' },

  /** Правило заряда манометров на первой странице фичи. */
  steamSpinRules: { key: 'steam_spin_rules', text: 'Every Wild Multiplier charges the gauge under its reel. A line that runs through it pays as many times more as the multiplier shows.' },

  /** Правило самого бонуса на второй странице фичи. */
  steamSpinBonusRules: { key: 'steam_spin_bonus_rules', text: 'Full gauges on all three reels start the bonus. The mini slot above the field lands three matching symbols, and its win grows by the sum of the gauges.' },

  /** Имя фичи Red Hot Spin заголовком страницы. Своё, как и у Steam Spin. */
  redHotSpinName: { key: 'red_hot_spin', text: 'RED HOT SPIN' },

  /** Правило Red Hot Spin на его странице правил. */
  redHotSpinRules: { key: 'red_hot_spin_rules', text: 'Three scatters anywhere on the field overheat every gauge at once. The mini slot spins with all gauges at the top, and pays by their sum.' },

  /** Строка про скаттер на странице особых символов. В паках такой строки нет, есть только про wild. */
  scatterRules: { key: 'scatter_rules', text: '-SCATTER: It pays wherever it lands on the field, not only along a payline.' },

  /** Строка про линии на странице линий выплат: выбирать их игроку не дают. */
  linesActive: { key: 'all_lines_active', text: 'Every payline is active on every spin. A line pays from the leftmost reel to the right.' },

  // Общие правила игровых автоматов. Ключи и текст пришли готовыми в паках, поэтому имена
  // здесь названы по содержанию строки, а не по номеру её ключа.

  /** Заголовок раздела об игровых автоматах вообще. */
  aboutTitle: { key: 'rules_title_1', text: 'About slot games' },

  /** Что такое игра на удачу. */
  aboutGame: { key: 'rules_text_1', text: 'This is a game of chance, with fixed odds, where the player can bet a certain amount of money or credits to try to get combinations of symbols that grant payments.' },

  /** Что такое барабаны. */
  aboutReels: { key: 'rules_text_1_2', text: 'The game presents a number of reels or columns, each with a set of symbols that, on each play, stop at a random position, defining which symbols are visible on the screen. Each game has different symbols, both in quantity and appearance, according to its theme and design.' },

  /** Заголовок раздела о полях под машиной. */
  basicsTitle: { key: 'rules_title_2', text: 'Basic info' },

  /** Где эти поля стоят. */
  basicsFields: { key: 'rules_text_2_2', text: 'Usually placed in the upper or lower side of the game, the player can find the session info, which can include:' },

  /** Что такое счёт. */
  basicsBalance: { key: 'rules_text_2_3', text: '-BALANCE: The money or credits the player has in his casino account, available to wager.' },

  /** Что такое ставка. */
  basicsStake: { key: 'rules_text_2_4', text: '-STAKE: The value set as the current bet.' },

  /** Что такое выигрыш последнего раунда. */
  basicsLastWin: { key: 'rules_text_2_5', text: '-LAST WIN: The value earned on the last play.' },

  /** Заголовок раздела о выплатах. */
  payoutsTitle: { key: 'rules_title_3', text: 'Payouts' },

  /** За что игра платит. */
  payoutsText: { key: 'rules_text_3', text: 'A specified number of like symbols appearing in a selected payline results in a payout. The number of each symbol required, its payout and the paylines patterns available in this game are explained in the Help/Paytable of the game itself.' },

  /** Заголовок раздела о линиях выплат. */
  linesTitle: { key: 'rules_title_4', text: 'Paylines' },

  /** Что такое линия выплат. */
  linesText: { key: 'rules_text_4', text: 'The paylines are numbered starting from 1. Some slot machines activate all paylines by default, and others allow player to select the number of active paylines. The amount of active paylines are displayed on the interface of the game and also the controls used for increasing/decreasing the active paylines, if applicable.' },

  /** Заголовок раздела о выборе ставки. */
  betTitle: { key: 'rules_title_5', text: 'Bet settings' },

  /** Как игрок выбирает ставку. */
  betText: { key: 'rules_text_5', text: 'Games allow player to choose the amount of coins, credits or money to wager. The player can modify the bet using the controls in the bet section (BET/LINE BET/TOTAL BET). In any case, the total wagered per spin is obtained by multiplying the number of coins selected (1 by default) by the coin value or line bet by the number of paylines selected. The bet level also defines the payouts level, according to the paytable.' },

  /** Заголовок раздела о ходе раунда. */
  playTitle: { key: 'rules_title_6', text: 'Playing the game' },

  /** Как играется раунд. */
  playText: { key: 'rules_text_6', text: "After the paylines and the bet amount have been set, the player has to put the reels in motion clicking or tapping the SPIN/START button, usually located at the center or right side of the screen. Clicking or tapping this button will start the reel spinning animation, and generate a debit in the player's balance. Once the reels stop, the prizes - if any - will be added to the balance." },

  /** Где игрок видит выигрыш. */
  playWinLabel: { key: 'rules_text_6_2', text: 'All wins are displayed on screen through the WIN/TOTAL WIN label.' },

  /** Заголовок раздела об особых символах. */
  specialTitle: { key: 'rules_title_9', text: 'Special symbols' },

  /** Что бывают особые символы. */
  specialText: { key: 'rules_text_9', text: 'In addition to regular symbols, the game may have one or more of the following special symbols:' },

  /** Что такое wild. */
  specialWild: { key: 'rules_text_9_2', text: '-WILD: It can substitute most other symbols in order to complete a winning combination.' },

  /** Куда смотреть за особыми символами этой игры. */
  specialHelp: { key: 'rules_text_9_4', text: 'In order to see the special symbols available in this game, please refer to the Help/Paytable of the game itself.' },

  /** Заголовок раздела о самой этой книжке. */
  helpTitle: { key: 'rules_title_11', text: 'Help/Paytable' },

  /** Что в этой книжке лежит. */
  helpText: { key: 'rules_text_11', text: "The player can find this section in the game as '?', 'info', 'help', 'paytable' or under similar names. It contains all the specific rules for this game, including definitions of symbols with its paytables, paylines, bonuses and special features descriptions. Legal info related to the game is displayed here as well." },

  /** Заголовок раздела о возврате игроку. */
  rtpTitle: { key: 'rules_title_12', text: 'Return to player (RTP)' },

  /** Где написан возврат игроку. */
  rtpText: { key: 'rules_text_12', text: 'The theoretical return to player value for this game is informed in the Help/Paytable of the game itself.' },
} as const satisfies Record<string, Phrase>;

export type PhraseId = keyof typeof PHRASES;

/** Пак перевода: ключ надписи и её текст на одном языке. */
export type Pack = Readonly<Record<string, unknown>>;

/**
 * Запасные шрифты под переведённую надпись.
 *
 * Шрифты игры латинские с кириллицей, см. assets/catalog.ts. Греческие, тайские и китайские
 * буквы им рисовать нечем, поэтому за ними надпись сходит к шрифтам системы.
 */
export const FALLBACK_FAMILIES: readonly string[] = ['system-ui', 'sans-serif'];

/** Собирает набор шрифтов под надпись: шрифт игры, а за незнакомыми буквами — шрифты системы. */
export function textFamily(family: string): string[] {
  return [family, ...FALLBACK_FAMILIES];
}

/** Перевод игры на один язык. */
export class Translation {
  /**
   * @param locale Язык, на котором игра идёт.
   * @param pack Пак этого языка. Пустой — пак не загрузился, и надписи пойдут английскими.
   * @param base Пак языка по умолчанию. Из него берутся надписи, которых нет в pack.
   */
  constructor(
    readonly locale: Locale,
    private readonly pack: Pack,
    private readonly base: Pack,
  ) {}

  /** Текст надписи на языке игры. */
  text(id: PhraseId): string {
    const phrase: Phrase = PHRASES[id];

    return line(this.pack, phrase.key) ?? line(this.base, phrase.key) ?? phrase.text;
  }
}

/**
 * Достаёт строку пака.
 *
 * Пак приходит с диска, и что в нём лежит, игра не решает. Пустая строка считается пропуском:
 * английская надпись читается лучше, чем пустое место на створке.
 */
function line(pack: Pack, key: string): string | null {
  const value: unknown = pack[key];

  return typeof value === 'string' && value !== '' ? value : null;
}

/** Игра до загрузки перевода: все надписи английские. */
let current = new Translation(DEFAULT_LOCALE, {}, {});
const listeners = new Set<() => void>();

/** Адрес пака перевода. Паки лежат в статике, поэтому адрес считается от корня сайта. */
function packUrl(locale: Locale): string {
  return `/locales-base/${locale}/game_translation.json`;
}

/**
 * Грузит пак одного языка.
 *
 * Сбой не бросает: игра без перевода идёт по-английски, а вот без экрана она не идёт вовсе.
 */
async function loadPack(locale: Locale): Promise<Pack> {
  try {
    const response: Response = await fetch(packUrl(locale), { headers: { accept: 'application/json' } });
    if (!response.ok)
      throw new Error(`Пак ${locale} отдан с кодом ${response.status}`);

    return (await response.json()) as Pack;
  } catch (error: unknown) {
    console.warn(`Перевод ${locale} не загрузился`, error);
    return {};
  }
}

/**
 * Грузит перевод на заданный язык вместе с английским паком под запасные надписи.
 *
 * Английский язык грузится одним паком: сходить за той же надписью дважды незачем.
 */
export async function loadTranslation(locale: Locale): Promise<Translation> {
  if (locale === DEFAULT_LOCALE) {
    const base: Pack = await loadPack(DEFAULT_LOCALE);
    return new Translation(locale, base, base);
  }

  const [pack, base]: [Pack, Pack] = await Promise.all([loadPack(locale), loadPack(DEFAULT_LOCALE)]);

  return new Translation(locale, pack, base);
}

/** Ставит перевод, на котором пойдёт игра. */
export function useTranslation(translation: Translation): void {
  current = translation;

  for (const listener of listeners)
    listener();
}

/** Подписывает UI на замену пакета перевода. Возвращает снятие подписки для destroy. */
export function listenTranslation(onChange: () => void): () => void {
  listeners.add(onChange);

  return (): void => {
    listeners.delete(onChange);
  };
}

/** Перевод, на котором игра идёт сейчас. */
export function translation(): Translation {
  return current;
}

/** Текст надписи на языке игры. */
export function text(id: PhraseId): string {
  return current.text(id);
}
